/* eslint-disable @typescript-eslint/no-explicit-any */
import { TwitterShareButton, LinkedinShareButton,InstapaperShareButton } from "react-share";
import moment from "moment";
import { DGAButton, getDgAButtonClassNames } from "components/Shared/dga-button/DGAButton";

// Extract only the path from PDF URL (remove domain)
export const getPdfPath = (url: string): string => {
  if (!url) return "";
  try {
    const urlObj = new URL(url);
    return urlObj.pathname + urlObj.search + urlObj.hash;
  } catch {
    // If URL parsing fails, check if it already starts with /
    if (url.startsWith("/")) {
      return url;
    }
    // Otherwise, try to extract path after domain
    const match = url.match(/\/\/[^\/]+(\/.*)$/);
    return match ? match[1] : url;
  }
};

interface FileDetailComponentProps {
  // Banner Title
  bannerTitle?: string;

  // Publish Date
  publishDateText?: string;
  publishedDate?: string | null;
  locale?: string;

  // Share Section
  shareText?: string;
  shareUrl: string;

  // Action Buttons
  previewBtnText?: string;
  downloadBtnText?: string;
  pdfUrl?: string;
  fileSrc?: string;
  fileName?: string;
  fileDisplayName?: string;

  // Handlers
  onPreviewClick?: () => void;
  onDownloadClick?: (event: React.MouseEvent, name: string) => void;
}

const FileDetailComponent = ({
  bannerTitle,
  publishDateText,
  publishedDate,
  locale = "en",
  shareText,
  shareUrl,
  previewBtnText,
  downloadBtnText,
  pdfUrl,
  fileSrc,
  fileName,
  fileDisplayName,
  onPreviewClick,
  onDownloadClick,
}: FileDetailComponentProps): JSX.Element => {
  // Date formatting
  const formattedPublishedDate = publishedDate
    ? (() => {
        const date = moment(publishedDate);
        return `${date.locale("en").format("D")} ${date
          .locale(locale === "ar" ? "ar-ly" : locale || "en")
          .format("MMM")} ${date.locale("en").format("YYYY")}`;
      })()
    : null;

  const downloadUrl = fileSrc ? getPdfPath(fileSrc) : "";
  const processedPdfUrl = pdfUrl ? getPdfPath(pdfUrl) : "";

  return (
    <div className="relative z-10 lg:max-w-[400px] lg:min-w-[300px] w-full lg:flex-shrink-0">
      <div className="animate-top-dga flex flex-col gap-5 bg-white rounded-2xl p-6 border border-neutral-300 lg:mt-20 mt-0 mb-10">
        {/* Banner Title */}
        {bannerTitle && (
          <h3 className="text-[22px] lg:text-[16px] font-bold text-neutral-800">{bannerTitle}</h3>
        )}

        {/* Publish Date */}
        {formattedPublishedDate && publishDateText && (
          <div className="flex flex-col gap-1">
            <div className="flex items-start gap-2">
              <img src="/Icons/calendar.svg" alt="Calendar" className="w-6 h-7 flex-shrink-0" />
              <div className="flex flex-col gap-1">
                <span className="text-[16px] font-bold text-neutral-800">{publishDateText}</span>
                <span className="text-[16px] text-primary-600">{formattedPublishedDate}</span>
              </div>
            </div>
          </div>
        )}

        {/* Share Section */}
        {shareText && (
          <div>
            <h4 className="text-[22px] lg:text-[16px] font-bold text-neutral-800">{shareText}</h4>
            <div className="flex gap-1 mt-1">
              <TwitterShareButton url={shareUrl}>
                <div className="w-fit flex items-center justify-center cursor-pointer hover:opacity-80 transition-opacity">
                  <img src="/Icons/twitter-x-sharemodal.svg" alt="Twitter/X" className="w-8 h-8" />
                </div>
              </TwitterShareButton>
              <LinkedinShareButton url={shareUrl}>
                <div className="w-fit flex items-center justify-center cursor-pointer hover:opacity-80 transition-opacity">
                  <img src="/Icons/linkedin-sharemodal.svg" alt="LinkedIn" className="w-8 h-8" />
                </div>
              </LinkedinShareButton>
              <InstapaperShareButton url={shareUrl}>
                <div className="w-fit flex items-center justify-center cursor-pointer hover:opacity-80 transition-opacity">
                  <img src="/Icons/instagram.svg" alt="Instagram" className="w-8 h-8" />
                </div>
              </InstapaperShareButton>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        {(processedPdfUrl || fileSrc) && (
          <div className="flex flex-row gap-3 border-t pt-6 border-neutral-400/50">
            {/* Preview Button */}
            {processedPdfUrl && previewBtnText && onPreviewClick && (
              <DGAButton
                className="!w-fit"
                type="button"
                onClick={onPreviewClick}
                buttonContent={
                  <div className="flex items-center justify-center gap-2">
                    <img src="/Icons/eye.svg" alt="Preview" className="w-6 h-6" />
                    <span>{previewBtnText}</span>
                  </div>
                }
                size="md"
                variant="primary"
              />
            )}

            {/* Download Button */}
            {fileSrc && downloadBtnText && (
              <a
                href={downloadUrl}
                download={fileName || fileDisplayName}
                onClick={(e: React.MouseEvent) => {
                  onDownloadClick?.(e, fileName || fileDisplayName || "");
                  e.stopPropagation();
                }}
                className={getDgAButtonClassNames("secondarySolid", "md", "!w-fit")}
              >
                <div className="flex items-center justify-center gap-2">
                  <img src="/Icons/download.svg" alt="" aria-hidden className="w-6 h-6" />
                  <span>{downloadBtnText}</span>
                </div>
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FileDetailComponent;

